import type { ReactNode } from 'react'
import { buttonClass, comingSoonTitle, COMING_SOON, type ButtonSize, type ButtonVariant } from '@/components/console/classes'
import { Icon, type IconName } from '@/components/icons/icon'
import { cn } from '@/lib/utils'

interface ComingSoonButtonProps {
  children: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: IconName
  iconOnly?: boolean
  /** Said after "Coming soon ·" in the tooltip, e.g. "porting ships with telephony". */
  reason?: string
  className?: string
  'aria-label'?: string
}

/**
 * A console button for an action with no backend yet. It looks disabled (the
 * design's opacity .5) and does nothing, but stays focusable so the reason in its
 * title -- and "Coming soon" for screen readers -- can be read. Never a toast that
 * pretends the action happened.
 */
export function ComingSoonButton({ children, variant, size, icon, iconOnly, reason, className, ...props }: ComingSoonButtonProps) {
  const title = comingSoonTitle(reason)
  return (
    <button
      type="button"
      aria-disabled="true"
      title={title}
      className={buttonClass({ variant, size, iconOnly }, cn('soon', className))}
      onClick={(event) => event.preventDefault()}
      {...props}
    >
      {icon ? <Icon name={icon} /> : null}
      {children}
      <span className="hide">{` · ${title}`}</span>
    </button>
  )
}

/** The design's quiet note under a coming-soon control (`.panelnote`): "Coming soon · not available yet". */
export function ComingSoonNote({ children = 'not available yet' }: { children?: ReactNode }) {
  return (
    <p className="panelnote">
      {COMING_SOON} · {children}
    </p>
  )
}
